/**
 * SQL 품질 검사 — 결과(메모 3)). 위: 검사 실행 · 거르기(규칙 칩 · 업무구분 · 심각도 · 검토 상태 · 글) / 왼쪽: 문장 목록 / 오른쪽: 상세
 * (원문 전체 + 걸린 부분 강조 · 발견 사항 · AI 판정과 검증 쿼리 · 검토 의견 · 같은 모양 문장).
 * AI 판정은 **고른 문장만**(한 번에 10건까지, 문장마다 한 번 부른다 · 중지 가능). 검증 쿼리는 사람이 「실행」 을 누를 때만 돈다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiPlay, FiCpu, FiSquare, FiSave, FiSearch, FiExternalLink, FiLayers, FiAlertTriangle } from 'react-icons/fi';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import { markSegments, CHECK_GROUPS, REVIEW_STATUS, maskForAgent } from '../../utils/sqlQualityMarks';
import AgentStrip from '../assistant/AgentStrip';
import * as api from '../../services/sqlQuality';

const MAX_AI_BATCH = 10;
const VIEW_STATES = ['flagged', 'clean', 'skipped', 'all'];
const fmt = (v) => (v == null ? '-' : Number(v).toLocaleString('en-US'));

function Highlighted({ sql, marks, activeKey }) {
  const segs = useMemo(() => markSegments(sql, marks, activeKey), [sql, marks, activeKey]);
  return (
    <pre className="sau-sql sqq-sql">
      {segs.map((s, i) => (s.mark
        // eslint-disable-next-line react/no-array-index-key -- 조각은 순서가 곧 키다
        ? <mark key={i} className={`sqq-mark${s.on ? ' on' : ''}`}>{s.text}</mark>
        // eslint-disable-next-line react/no-array-index-key -- 조각은 순서가 곧 키다
        : <React.Fragment key={i}>{s.text}</React.Fragment>))}
    </pre>
  );
}
Highlighted.propTypes = { sql: PropTypes.string.isRequired, marks: PropTypes.array.isRequired, activeKey: PropTypes.string };

/** 발견 사항 한 줄의 설명 — 검사기별 상세 값 */
export function findingText(f, t) {
  const d = f.detail || {};
  if (f.check === 'perf') return (d.over || []).map(o => t(`common:sqq.over.${o.key}`, { val: fmt(o.val), lim: fmt(o.lim) })).join(' · ');
  if (f.check === 'ansiJoin') return t(d.kind === 'oraclePlus' ? 'common:sqq.detail.oraclePlus' : 'common:sqq.detail.comma', { count: d.count });
  if (f.check === 'tableAlias') return (d.names || []).join(', ');
  if (f.check === 'keywordCase') return t('common:sqq.detail.case', { count: d.count, want: t(d.want === 'upper' ? 'common:sqq.caseUpper' : 'common:sqq.caseLower') });
  if (f.check === 'distinct' || f.check === 'scalarRownum') return t(`common:sqq.block.${d.block || 'main'}`, { defaultValue: d.block || '' });
  if (f.check === 'joinNoKey' || f.check === 'joinTypeMismatch') {
    const pairs = (d.pairs || []).map(p => `${p.l} = ${p.r}`).join(' · ');
    // 키가 아닌 조인의 근거 — 같은 값 무리로 이어진 컬럼과 덮지 못한 키(joinClosure). 왜 걸렸는지 사람이 대조할 수 있게
    const basis = (d.basis || []).map(b => t('common:sqq.joinBasis', { table: b.table, other: b.other || '-', linked: (b.linked || []).join(', ') || t('common:sqq.joinBasisNone'), keys: (b.keys || []).join(' / ') }));
    return [pairs, ...basis].join(' — ');
  }
  if (f.check === 'pattern') return `「${d.name}」 ${d.mode === 'similar' ? t('common:sqq.patternSimilar', { score: d.score }) : t('common:sqq.patternSame')}`;
  if (d.funcs) return d.funcs.join(', ');
  return '';
}

function VerdictCard({ v, index, item, job, connections, onRan, disabled }) {
  const { t } = useTranslation();
  const [sql, setSql] = useState(v.verifySql || '');
  const [conn, setConn] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const finding = (item.findings || []).find(f => f.id === v.ref);
  const title = v.ref.startsWith('R:') ? `${t('common:sqq.ruleRef')} — ${v.label || ''}` : `${v.ref} · ${finding ? t(`common:sqq.check.${finding.check}`) : ''}`;
  const run = async () => {
    setBusy(true); setRes(null);
    try { const r = await api.runVerify(job.id, item.id, { index, sql, connectionId: job.connectionId ? undefined : conn }); setRes(r.result); onRan(r.item); } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };
  return (
    <div className={`stn-card sqq-verdict v-${v.verdict}`}>
      <div className="stn-row stn-between">
        <span className="stn-row"><span className={`sqq-vbadge v-${v.verdict}`}>{t(`common:sqq.verdict.${v.verdict}`)}</span> <b>{title}</b></span>
        {v.run && <span className="stn-note">{t('common:sqq.lastVerify', { rows: v.run.rowCount, more: v.run.more ? '+' : '', at: storedTime(v.run.at, 'min') })}</span>}
      </div>
      {v.reason && <div className="sqq-reason">{v.reason}</div>}
      {v.snippet && <div className="stn-note">{t('common:sqq.snippet')}: <span className="mono">{v.snippet}</span></div>}
      {v.fix && <div className="stn-note">{t('common:sqq.fix')}: {v.fix}</div>}
      {(v.verifySql || sql) && (
        <div className="sqq-verify">
          <span className="stn-note">{t('common:sqq.verifyHint')}</span>
          <textarea className="srv-mono" rows={4} value={sql} onChange={e => setSql(e.target.value)} disabled={disabled || busy} />
          <div className="stn-row">
            {!job.connectionId && (
              <select value={conn} onChange={e => setConn(e.target.value)} aria-label={t('common:sqq.connection')}>
                <option value="">{t('common:sqq.pickConnection')}</option>
                {(connections || []).map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
              </select>
            )}
            <button type="button" className="app-btn" onClick={run} disabled={disabled || busy || !sql.trim() || (!job.connectionId && !conn)}><FiPlay size={12} /> {busy ? t('common:sqq.running') : t('common:sqq.runVerify')}</button>
          </div>
          {res && (
            <div className="stn-table-wrap sqq-fade">
              <table className="stn-table">
                <thead><tr>{res.columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>
                  {res.rows.map((r, i) => (
                    // eslint-disable-next-line react/no-array-index-key -- 결과 행은 순서가 곧 키다
                    <tr key={i}>{r.map((x, j) => <td key={res.columns[j] || j} className="mono">{x == null ? '(null)' : String(x)}</td>)}</tr>
                  ))}
                  {!res.rows.length && <tr><td colSpan={Math.max(1, res.columns.length)} className="stn-note">{t('common:sqq.noRows')}</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
VerdictCard.propTypes = { v: PropTypes.object.isRequired, index: PropTypes.number.isRequired, item: PropTypes.object.isRequired, job: PropTypes.object.isRequired, connections: PropTypes.array, onRan: PropTypes.func.isRequired, disabled: PropTypes.bool };

const QualityResults = ({ job, onJobChanged, aiEnabled, aiProvider, connections, onOpenInEditor, onOpenBuilder }) => {
  const { t } = useTranslation();
  // 샘플 검사는 걸리면 안 되는 샘플(정상)도 봐야 한다 — 처음부터 다 보인다
  // state — 보기: flagged 걸린 것 · clean 걸리지 않은 것 · skipped 판정 안 한 조인이 있는 것 · all 전체(2026-10-31 — 「걸린 것만」 체크 하나로는 나머지가 왜 없는지 알 수 없었다)
  const [filter, setFilter] = useState({ checks: [], biz: '', severity: '', review: '', q: '', state: job.target && job.target.source === 'sample' ? 'all' : 'flagged' });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [itemId, setItemId] = useState(null);
  const [item, setItem] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [similar, setSimilar] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(null);
  const [progress, setProgress] = useState(null);
  const [review, setReviewForm] = useState({ status: 'todo', note: '' });
  const stopRef = useRef(null);
  const tRef = useRef(t);
  tRef.current = t;

  const load = useCallback(async () => {
    if (!job.summary) { setRows([]); setTotal(0); return; }
    try { const r = await api.listItems(job.id, filter); setRows(r.rows || []); setTotal(r.total || 0); } catch (err) { toast.error(formatApiError(err, tRef.current)); }
  }, [job.id, job.summary, filter]);
  useEffect(() => { load(); }, [load]);

  const openItem = useCallback(async (id) => {
    setItemId(id); setActiveKey(null); setSimilar(null);
    try {
      const r = await api.getItem(job.id, id);
      setItem(r.item);
      setReviewForm({ status: (r.item.review && r.item.review.status) || 'todo', note: (r.item.review && r.item.review.note) || '' });
    } catch (err) { toast.error(formatApiError(err, tRef.current)); }
  }, [job.id]);

  const runCheck = async () => {
    setBusy('check');
    try { const r = await api.runCheck(job.id); onJobChanged(r.job); toast.success(t('common:sqq.checked', { flagged: r.job.summary.flagged, items: r.job.summary.items })); } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(null); }
  };

  const analyze = async (ids) => {
    const list = ids.slice(0, MAX_AI_BATCH);
    setBusy('ai');
    try {
      for (let i = 0; i < list.length; i += 1) {
        const ctl = new AbortController();
        stopRef.current = ctl;
        setProgress({ n: i + 1, of: list.length });
        try {
          // eslint-disable-next-line no-await-in-loop -- 문장마다 한 번씩, 차례로(중지하면 그 자리에서 멈춘다)
          const r = await api.aiAnalyze(job.id, list[i], { provider: aiProvider }, ctl.signal);
          if (list[i] === itemId) setItem(r.item);
          if ((r.rejected || []).length) toast.warning(t('common:sqq.aiRejected', { n: r.rejected.length }));
        } catch (err) {
          if (ctl.signal.aborted) { toast.info(t('common:sqq.stopped')); break; }
          toast.error(formatApiError(err, t));
          break;
        }
      }
      // eslint-disable-next-line no-await-in-loop -- 위 반복이 끝난 뒤 한 번
      const j = await api.getJob(job.id);
      onJobChanged(j.job);
      setPicked(new Set());
    } finally { setBusy(null); setProgress(null); stopRef.current = null; }
  };

  const saveReview = async () => {
    try { const r = await api.setReview(job.id, item.id, review); setItem(r.item); const j = await api.getJob(job.id); onJobChanged(j.job); toast.success(t('common:sqq.saved'), { autoClose: 1000 }); } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const loadSimilar = async () => { try { setSimilar((await api.similarItems(job.id, item.id)).similar || []); } catch (err) { toast.error(formatApiError(err, t)); } };

  const sum = job.summary;
  /**
   * 규칙 칩(2026-10-31 사용자 요청) — 한 번 누르면 라디오처럼 그 규칙만(그것만 켜져 있으면 풀어서 전체), 두 번 누르면 예전처럼 그 칩만 켜고 끈다(여럿 함께).
   * 두 번째 누름(e.detail === 2)은 첫 누름이 바꾸기 **전**의 선택에 토글을 건다 — 안 그러면 첫 누름의 라디오가 다른 칩을 지운 뒤라 함께 고를 수 없다
   */
  const chipBefore = useRef([]);
  const clickCheck = (id, e) => {
    if (e && e.detail >= 2) {
      const base = chipBefore.current;
      setFilter(f => ({ ...f, checks: base.includes(id) ? base.filter(x => x !== id) : [...base, id] }));
      return;
    }
    chipBefore.current = filter.checks;
    setFilter(f => ({ ...f, checks: f.checks.length === 1 && f.checks[0] === id ? [] : [id] }));
  };
  const chipCount = (id) => {
    const n = (sum.byCheck || {})[id] || 0;
    // 걸린 곳 수는 이 판부터의 검사 결과에만 있다 — 없으면 문장 수만(다시 검사하면 생긴다)
    return sum.byCheckPlaces ? t('common:sqq.chipCount', { n, p: sum.byCheckPlaces[id] || 0 }) : t('common:sqq.chipCountN', { n });
  };
  const togglePick = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else if (n.size < MAX_AI_BATCH) n.add(id); return n; });
  const marks = item ? [
    ...(item.findings || []).filter(f => (f.ranges || []).length).map(f => ({ key: f.id, ranges: f.ranges })),
    ...((item.ai && item.ai.verdicts) || []).filter(v => (v.ranges || []).length).map(v => ({ key: v.ref, ranges: v.ranges })),
  ] : [];
  const bizList = sum ? Object.keys(sum.byBiz || {}) : [];

  return (
    <div className="stn-step sqq-results">
      <div className="stn-row stn-between">
        <span className="stn-row">
          <button type="button" className="app-btn app-btn-primary" onClick={runCheck} disabled={!!busy || !job.target}><FiPlay size={12} /> {sum ? t('common:sqq.recheck') : t('common:sqq.runCheck')}</button>
          {sum && <span className="stn-note">{t('common:sqq.summaryLine', { flagged: sum.flagged, items: sum.items, analyzed: sum.analyzed || 0, reviewed: sum.reviewed || 0 })}</span>}
          {!job.target && <span className="stn-note">{t('common:sqq.needTarget')}</span>}
        </span>
        {aiEnabled && sum && (busy === 'ai'
          ? <button type="button" className="app-btn app-btn-danger" onClick={() => stopRef.current && stopRef.current.abort()}><FiSquare size={12} /> {t('common:sqq.stop')} {progress ? `${progress.n}/${progress.of}` : ''}</button>
          : <button type="button" className="app-btn stn-ai" onClick={() => analyze([...picked])} disabled={!!busy || !picked.size} title={t('common:sqq.aiBatchHint', { max: MAX_AI_BATCH })}><FiCpu size={12} /> {t('common:sqq.aiBatch', { n: picked.size })}</button>)}
      </div>
      {sum && (
        <>
          <div className="sqq-chips" role="group" aria-label={t('common:sqq.filterChecks')}>
            {CHECK_GROUPS.map(gr => gr.checks.filter(id => (job.criteria.checks || {})[id] && job.criteria.checks[id].on).map(id => (
              <button key={id} type="button" className={`sqq-chip g-${gr.id}${filter.checks.includes(id) ? ' on' : ''}`} aria-pressed={filter.checks.includes(id)} title={t('common:sqq.chipHint')} onClick={e => clickCheck(id, e)}>
                {t(`common:sqq.check.${id}`)} <span className="sqq-chip-n">{chipCount(id)}</span>
              </button>
            )))}
            {(job.criteria.patterns || []).length > 0 && (
              <button type="button" className={`sqq-chip g-pattern${filter.checks.includes('pattern') ? ' on' : ''}`} aria-pressed={filter.checks.includes('pattern')} title={t('common:sqq.chipHint')} onClick={e => clickCheck('pattern', e)}>
                {t('common:sqq.check.pattern')} <span className="sqq-chip-n">{chipCount('pattern')}</span>
              </button>
            )}
            {(job.criteria.guideRules || []).length > 0 && (
              <button type="button" className={`sqq-chip g-rule${filter.checks.includes('rule') ? ' on' : ''}`} aria-pressed={filter.checks.includes('rule')} title={t('common:sqq.chipHint')} onClick={e => clickCheck('rule', e)}>{t('common:sqq.ruleRef')}</button>
            )}
          </div>
          {(sum.notes || []).map(n => (
            <div key={n} className="sau-notice"><FiAlertTriangle size={11} /> {t(`common:sqq.checkNote.${n.split(':')[0]}`, { defaultValue: n, detail: n.split(':').slice(1).join(':') })}
              {/* 판정하지 않은 조인이 있는 문장으로 바로 — 합계만으로는 어느 문장인지 몰랐다(2026-10-31) */}
              {n.startsWith('MODEL_NO_KEYS:') && <button type="button" className="app-btn sqq-note-btn" onClick={() => setFilter(f => ({ ...f, state: 'skipped', checks: [] }))}>{t('common:sqq.showSkipped')}</button>}
            </div>
          ))}
          {sum.catalogTables != null && <div className="stn-note">{t('common:sqq.catalogLoaded', { n: sum.catalogTables })}</div>}
          <div className="stn-row sqq-filters">
            <select value={filter.biz} onChange={e => setFilter({ ...filter, biz: e.target.value })} aria-label={t('common:sqq.biz')}>
              <option value="">{t('common:sqq.allBiz')}</option>
              {bizList.map(b => <option key={b || '-'} value={b}>{b || t('common:sqq.noBiz')}</option>)}
            </select>
            <select value={filter.severity} onChange={e => setFilter({ ...filter, severity: e.target.value })} aria-label={t('common:sqq.severity')}>
              <option value="">{t('common:sqq.allSeverity')}</option>
              {['high', 'mid', 'low'].map(s => <option key={s} value={s}>{t(`common:sqq.sev.${s}`)}</option>)}
            </select>
            <select value={filter.review} onChange={e => setFilter({ ...filter, review: e.target.value })} aria-label={t('common:sqq.review')}>
              <option value="">{t('common:sqq.allReview')}</option>
              {REVIEW_STATUS.map(s => <option key={s} value={s}>{t(`common:sqq.reviewStatus.${s}`)}</option>)}
            </select>
            <select value={filter.state} onChange={e => setFilter({ ...filter, state: e.target.value })} aria-label={t('common:sqq.viewLabel')}>
              {VIEW_STATES.map(v => <option key={v} value={v}>{t(`common:sqq.viewState.${v}`, { n: { flagged: sum.flagged, clean: sum.items - sum.flagged, all: sum.items }[v] })}</option>)}
            </select>
            <input className="stn-grow" defaultValue={filter.q} placeholder={t('common:sqq.searchPh')} onKeyDown={e => { if (e.key === 'Enter') setFilter({ ...filter, q: e.target.value }); }} />
            <span className="stn-note">{t('common:sqq.shown', { n: rows.length, total })}</span>
          </div>
          <div className="sqq-split">
            <ul className="sqq-list">
              {rows.map(r => (
                <li key={r.id} className={`${itemId === r.id ? 'on' : ''}`} onClick={() => openItem(r.id)}>
                  {aiEnabled && <input type="checkbox" checked={picked.has(r.id)} onClick={e => e.stopPropagation()} onChange={() => togglePick(r.id)} aria-label={t('common:sqq.pickForAi')} />}
                  <div className="sqq-li-body">
                    <div className="stn-row">
                      {r.worst && <span className={`sqq-sev s-${r.worst}`}>{t(`common:sqq.sev.${r.worst}`)}</span>}
                      <b className="mono">#{r.seq}{r.sqlId ? ` ${r.sqlId}` : ''}</b>
                      {r.biz && <span className="sqq-tag">{r.biz}</span>}
                      {r.ai && <span className="sqq-tag sqq-tag-ai">AI</span>}
                      {r.review && r.review !== 'todo' && <span className={`sqq-tag rv-${r.review}`}>{t(`common:sqq.reviewStatus.${r.review}`)}</span>}
                      {r.skips && r.skips.undeclaredJoins > 0 && <span className="sqq-tag sqq-tag-skip" title={t('common:sqq.skipDetail', { n: r.skips.undeclaredJoins })}>{t('common:sqq.skipTag', { n: r.skips.undeclaredJoins })}</span>}
                      {r.sample && r.sample.expect && <span className={`sqq-tag ${r.sample.ok ? 'sqq-sample-ok' : 'sqq-sample-bad'}`}>{t(r.sample.ok ? 'common:sqq.sampleOk' : 'common:sqq.sampleBad')}</span>}
                    </div>
                    <div className="sqq-li-checks">{r.checks.map(c => t(`common:sqq.check.${c}`)).join(' · ')}</div>
                    <div className="sqq-li-head mono">{r.head}</div>
                  </div>
                </li>
              ))}
              {!rows.length && <li className="stn-empty">{t('common:sqq.noRowsFilter')}</li>}
            </ul>
            <div className="sqq-detail">
              {!item && <div className="stn-empty-main">{t('common:sqq.pickItem')}</div>}
              {item && (
                <div className="sqq-fade" key={item.id}>
                  <div className="stn-row stn-between">
                    <span className="stn-row">
                      <b className="mono">#{item.seq}{item.sqlId ? ` · ${item.sqlId}` : ''}</b>
                      <span className="sqq-tag">{t(`common:sqq.src.${item.source}`)}</span>
                      {item.biz && <span className="sqq-tag">{item.biz}</span>}
                      {item.masked && <span className="sqq-tag" title={t('common:sqq.maskedHint')}>{t('common:sqq.masked')}</span>}
                    </span>
                    <span className="stn-row">
                      {onOpenBuilder && <button type="button" className="app-btn" onClick={() => onOpenBuilder(item.sql)}><FiLayers size={12} /> {t('common:sqq.openBuilder')}</button>}
                      {onOpenInEditor && <button type="button" className="app-btn" onClick={() => onOpenInEditor(item.sql)}><FiExternalLink size={12} /> {t('common:sqq.openEditor')}</button>}
                    </span>
                  </div>
                  {/* 샘플 검사 — 이 샘플이 보여 주는 것과 기대 결과, 실제 검사기 결과와의 대조 */}
                  {item.sample && item.sample.expect && (
                    <div className={`sqq-sample ${item.sample.ok ? 'ok' : 'bad'}`}>
                      <b>{item.sample.title}</b>
                      <div>{t('common:sqq.sampleExpect', { list: item.sample.expect.length ? item.sample.expect.map(c => t(`common:sqq.check.${c}`)).join(' · ') : t('common:sqq.sampleExpectNone') })}</div>
                      <div>{item.sample.ok ? t('common:sqq.sampleMatch') : t('common:sqq.sampleDiff', {
                        missing: item.sample.missing.map(c => t(`common:sqq.check.${c}`)).join(' · ') || '-',
                        extra: item.sample.extra.map(c => t(`common:sqq.check.${c}`)).join(' · ') || '-',
                      })}</div>
                    </div>
                  )}
                  {item.stats && item.stats.executions != null && (
                    <div className="stn-note">{t('common:sqq.statsLine', { exec: fmt(item.stats.executions), avg: item.stats.avgElapsedSec ?? '-', buf: fmt(item.stats.bufPerExec), disk: fmt(item.stats.diskPerExec) })}{item.stats.maxIntervalSec != null ? ` · ${t('common:sqq.maxInterval', { sec: item.stats.maxIntervalSec })}` : ''}</div>
                  )}
                  <Highlighted sql={item.sql} marks={marks} activeKey={activeKey} />
                  <div className="sqq-findings">
                    {(item.findings || []).map(f => (
                      <button key={f.id} type="button" className={`sqq-finding${activeKey === f.id ? ' on' : ''}`} onClick={() => setActiveKey(k => (k === f.id ? null : f.id))}>
                        <span className={`sqq-sev s-${f.severity}`}>{t(`common:sqq.sev.${f.severity}`)}</span>
                        <b>{f.id}</b> {t(`common:sqq.check.${f.check}`)} <span className="stn-note">{findingText(f, t)}</span>
                      </button>
                    ))}
                    {!(item.findings || []).length && <span className="stn-note">{t('common:sqq.noFindings')}</span>}
                  </div>
                  {/* 이 문장에서 판정하지 않은 것·다른 길로 적합으로 본 것 — 「걸린 것 없음」 이 「다 봤다」 로 읽히지 않게 */}
                  {item.skips && item.skips.undeclaredJoins > 0 && <div className="sau-notice"><FiAlertTriangle size={11} /> {t('common:sqq.skipDetail', { n: item.skips.undeclaredJoins })}</div>}
                  {item.skips && item.skips.transitiveJoins > 0 && <div className="stn-note">{t('common:sqq.transDetail', { n: item.skips.transitiveJoins })}</div>}
                  {/* 이 창이 AI 패널을 가린다 — 같은 대화로 이 문장을 두고 묻는다(값은 가려서, 그 턴에만). 패널이 없으면 그리지 않는다 */}
                  {aiEnabled && <AgentStrip placeholder={t('common:sqq.askAgentPh')} getScreen={() => ({ kind: 'sqlQuality', text: JSON.stringify({
                    sql: maskForAgent(item.sql), biz: item.biz || null, stats: item.stats || null,
                    findings: (item.findings || []).map(f => ({ id: f.id, check: f.check, severity: f.severity, detail: f.detail })),
                    verdicts: ((item.ai && item.ai.verdicts) || []).map(v => ({ ref: v.ref, verdict: v.verdict, reason: v.reason })),
                    review: item.review ? { status: item.review.status, note: item.review.note } : null,
                    rules: (job.criteria.guideRules || []).map((r, i) => `R${i + 1}: ${r.principle}`),
                  }, null, 1) })} />}
                  <div className="stn-box">
                    <div className="stn-row stn-between">
                      <b>{t('common:sqq.aiTitle')}</b>
                      {aiEnabled && (busy === 'ai'
                        ? <span className="stn-note">{t('common:sqq.aiRunning')}</span>
                        : <button type="button" className="app-btn stn-ai" onClick={() => analyze([item.id])} disabled={!!busy}><FiCpu size={12} /> {item.ai ? t('common:sqq.aiAgain') : t('common:sqq.aiOne')}</button>)}
                    </div>
                    {!item.ai && <span className="stn-note">{aiEnabled ? t('common:sqq.aiHint') : t('common:sqq.aiOff')}</span>}
                    {item.ai && item.ai.summary && <div className="sqq-reason">{item.ai.summary}</div>}
                    {((item.ai && item.ai.verdicts) || []).map((v, i) => (
                      <VerdictCard key={`${item.id}-${v.ref}`} v={v} index={i} item={item} job={job} connections={connections} disabled={!!busy} onRan={setItem} />
                    ))}
                  </div>
                  <div className="stn-box">
                    <b>{t('common:sqq.reviewTitle')}</b>
                    <div className="stn-row stn-report-mode" role="group" aria-label={t('common:sqq.review')}>
                      {REVIEW_STATUS.map(s => (
                        <button key={s} type="button" className={`app-btn${review.status === s ? ' on' : ''}`} aria-pressed={review.status === s} onClick={() => setReviewForm({ ...review, status: s })}>{t(`common:sqq.reviewStatus.${s}`)}</button>
                      ))}
                    </div>
                    <textarea rows={2} value={review.note} placeholder={t('common:sqq.reviewNotePh')} onChange={e => setReviewForm({ ...review, note: e.target.value })} />
                    <div><button type="button" className="app-btn app-btn-primary" onClick={saveReview}><FiSave size={12} /> {t('common:sqq.save')}</button></div>
                  </div>
                  <div className="stn-box">
                    <div className="stn-row stn-between">
                      <b>{t('common:sqq.similarTitle')}</b>
                      <button type="button" className="app-btn" onClick={loadSimilar}><FiSearch size={12} /> {t('common:sqq.findSimilar')}</button>
                    </div>
                    <span className="stn-note">{t('common:sqq.similarHint')}</span>
                    {similar && !similar.length && <span className="stn-note">{t('common:sqq.noSimilar')}</span>}
                    {similar && similar.map(s => (
                      <div key={s.id} className="sqq-sim" onClick={() => openItem(s.id)} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') openItem(s.id); }}>
                        <span className="sqq-tag">{t(`common:sqq.simKind.${s.kind}`)}</span>
                        <b className="mono">#{s.seq}{s.sqlId ? ` ${s.sqlId}` : ''}</b>
                        <span className="stn-note">{s.checks.map(c => t(`common:sqq.check.${c}`)).join(' · ') || t('common:sqq.noFindings')}</span>
                        {s.ai && <span className="sqq-tag sqq-tag-ai">{s.ai.map(v => t(`common:sqq.verdict.${v}`)).join(', ')}</span>}
                        {s.review && s.review !== 'todo' && <span className={`sqq-tag rv-${s.review}`}>{t(`common:sqq.reviewStatus.${s.review}`)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

QualityResults.propTypes = {
  job: PropTypes.object.isRequired,
  onJobChanged: PropTypes.func.isRequired,
  aiEnabled: PropTypes.bool,
  aiProvider: PropTypes.string,
  connections: PropTypes.array,
  onOpenInEditor: PropTypes.func,
  onOpenBuilder: PropTypes.func,
};

export default QualityResults;
